import { useState, useRef, useCallback, useEffect } from 'react';
import { debug } from '@/utils/debug';

// Polling configuration matching the original frontend
export const POLLING_CONFIG = {
  FAST_INTERVAL: 4000,      // 4 seconds
  FAST_ATTEMPTS: 20,        // 80 seconds total (20 * 4s)
  SLOW_INTERVAL: 20000,     // 20 seconds  
  SLOW_ATTEMPTS: 24,        // 8 minutes total (24 * 20s)
  INITIAL_DELAY: 6000,      // 6 seconds before first poll
} as const;

export type PollingPhase = 'fast' | 'slow';
export type PollingStatus = 'idle' | 'starting' | 'polling' | 'success' | 'timeout' | 'error';

export interface PollingState {
  status: PollingStatus;
  phase: PollingPhase;
  attempts: number;
  totalAttempts: number;
  isPolling: boolean;
  error: string | null;
  statusMessage: string;
}

export interface PollingConfig {
  onSuccess: (result: unknown) => void;
  onTimeout: (customerId: string) => void;
  onError: (error: string) => void;
  customerId: string;
}

/**
 * Adaptive polling hook that implements the signature Flouri polling system:
 * - Fast phase: 4s intervals for 80 seconds (20 attempts)
 * - Slow phase: 20s intervals for 8 minutes (24 attempts) 
 * - Race condition protection
 * - Automatic cleanup
 */
export function usePolling(
  pollFunction: (customerId: string) => Promise<boolean>,
  config: PollingConfig
) {
  const [state, setState] = useState<PollingState>({
    status: 'idle',
    phase: 'fast',
    attempts: 0,
    totalAttempts: 0,
    isPolling: false,
    error: null,
    statusMessage: '',
  });

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentCustomerIdRef = useRef<string | null>(null);
  const isPollingRef = useRef(false);

  // Refs to track state values for use in callbacks to avoid stale closures
  const phaseRef = useRef<PollingPhase>(state.phase);
  const totalAttemptsRef = useRef(state.totalAttempts);

  // Keep refs in sync with state
  useEffect(() => {
    phaseRef.current = state.phase;
    totalAttemptsRef.current = state.totalAttempts;
  }, [state.phase, state.totalAttempts]);

  // Calculate max attempts
  const maxAttempts = POLLING_CONFIG.FAST_ATTEMPTS + POLLING_CONFIG.SLOW_ATTEMPTS;

  // Update status message
  const updateStatusMessage = useCallback((message: string) => {
    setState(prev => ({ ...prev, statusMessage: message }));
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    debug.log('usePolling: Cleaning up polling session');
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    isPollingRef.current = false;
    currentCustomerIdRef.current = null;
    
    setState(prev => ({
      ...prev,
      status: 'idle',
      isPolling: false,
      attempts: 0,
      totalAttempts: 0,
      phase: 'fast',
      error: null,
    }));
  }, []);

  // Store poll function in a ref to avoid stale closures in setTimeout
  const pollRef = useRef<((customerId: string) => Promise<void>) | null>(null);

  // Main polling function - uses refs for state to avoid stale closures
  const poll = useCallback(async (customerId: string) => {
    // Race condition protection
    if (!isPollingRef.current || currentCustomerIdRef.current !== customerId) {
      debug.warn('usePolling: Polling session mismatch or inactive', {
        expected: customerId,
        current: currentCustomerIdRef.current,
        isPolling: isPollingRef.current,
      });
      return;
    }

    // Read current values from refs to avoid stale closures
    const currentPhase = phaseRef.current;
    const currentTotalAttempts = totalAttemptsRef.current;

    try {
      debug.log('usePolling: Attempting poll', {
        customerId,
        attempt: currentTotalAttempts + 1,
        phase: currentPhase,
      });

      // Calculate current attempts first
      const newTotalAttempts = currentTotalAttempts + 1;

      // Update attempt count
      setState(prev => ({
        ...prev,
        totalAttempts: newTotalAttempts,
        attempts: prev.phase === 'fast'
          ? prev.attempts + 1
          : prev.attempts,
      }));

      // Call the poll function
      const result = await pollFunction(customerId);

      if (result) {
        // Success! Stop polling
        debug.log('usePolling: Poll successful, stopping');
        cleanup();
        setState(prev => ({ ...prev, status: 'success' }));
        config.onSuccess(result);
        return;
      }

      // Continue polling - check if we need to switch phases or timeout

      if (newTotalAttempts >= maxAttempts) {
        // Max attempts reached - timeout
        debug.log('usePolling: Max attempts reached, timing out');
        cleanup();
        setState(prev => ({ ...prev, status: 'timeout' }));
        config.onTimeout(customerId);
        return;
      }

      // Check if we need to switch to slow phase
      if (newTotalAttempts >= POLLING_CONFIG.FAST_ATTEMPTS && currentPhase === 'fast') {
        debug.log('usePolling: Switching to slow phase');
        setState(prev => ({
          ...prev,
          phase: 'slow',
          attempts: 0, // Reset phase-specific attempts
        }));
      }

      // Determine interval based on phase (use updated phase after switch check)
      const nextPhase = newTotalAttempts >= POLLING_CONFIG.FAST_ATTEMPTS ? 'slow' : currentPhase;
      const interval = nextPhase === 'fast'
        ? POLLING_CONFIG.FAST_INTERVAL
        : POLLING_CONFIG.SLOW_INTERVAL;
      updateStatusMessage('Waiting for invitation acceptance...');

      // Use pollRef to always call the latest version of poll
      timeoutRef.current = setTimeout(() => {
        pollRef.current?.(customerId);
      }, interval);

      debug.log('usePolling: Scheduled next poll', { interval, customerId });

    } catch (error) {
      debug.error('usePolling: Poll failed', error);

      // Continue polling on network errors (transient failures)
      const currentPhaseForError = phaseRef.current;
      const interval = currentPhaseForError === 'fast'
        ? POLLING_CONFIG.FAST_INTERVAL
        : POLLING_CONFIG.SLOW_INTERVAL;
      updateStatusMessage('Waiting for invitation acceptance...');

      // Use pollRef to always call the latest version of poll
      timeoutRef.current = setTimeout(() => {
        pollRef.current?.(customerId);
      }, interval);
    }
  }, [pollFunction, config, cleanup, maxAttempts, updateStatusMessage]);

  // Keep pollRef in sync with the latest poll function
  useEffect(() => {
    pollRef.current = poll;
  }, [poll]);

  // Start polling
  const startPolling = useCallback((customerId: string) => {
    debug.log('usePolling: Starting polling session', { customerId });

    // Prevent duplicate polling sessions
    if (isPollingRef.current) {
      debug.warn('usePolling: Polling already active, cannot start new session');
      return false;
    }

    // Initialize polling session
    isPollingRef.current = true;
    currentCustomerIdRef.current = customerId;

    setState({
      status: 'starting',
      phase: 'fast',
      attempts: 0,
      totalAttempts: 0,
      isPolling: true,
      error: null,
      statusMessage: 'Waiting for invitation acceptance...',
    });

    // Start with initial delay
    timeoutRef.current = setTimeout(() => {
      setState(prev => ({ ...prev, status: 'polling' }));
      poll(customerId);
    }, POLLING_CONFIG.INITIAL_DELAY);

    return true;
  }, [poll]);

  // Stop polling
  const stopPolling = useCallback(() => {
    debug.log('usePolling: Stopping polling session');
    cleanup();
  }, [cleanup]);

  // Manual check (immediate poll)
  const manualCheck = useCallback(async (customerId: string) => {
    debug.log('usePolling: Manual check requested', { customerId });

    // If not polling, start a temporary session
    if (!isPollingRef.current) {
      if (startPolling(customerId)) {
        // Wait for the initial delay, then poll immediately
        setTimeout(() => {
          poll(customerId);
        }, 1000); // Short delay for UI feedback
      }
    } else if (currentCustomerIdRef.current === customerId) {
      // Use existing session
      await poll(customerId);
    } else {
      debug.warn('usePolling: Cannot manual check - polling active for different customer');
    }
  }, [startPolling, poll]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    state,
    startPolling,
    stopPolling,
    manualCheck,
    updateStatusMessage,
    maxAttempts,
    fastAttempts: POLLING_CONFIG.FAST_ATTEMPTS,
  };
}